import {
  useState,
  useCallback,
  useRef,
  PropsWithChildren,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';

import { configSelector } from 'common/selectors/config';
import { getQueryParamsFromUrl } from 'common/helpers/Url';

import loadLanguage, { detectLanguage } from './loadLanguage';
import TranslationContext from './TranslationContext';
import createGetKey from './createGetKey';

const Trans = ({ children }: PropsWithChildren) => {
  const [translationsByLocale, setTranslationsByLocale] = useState({});
  const [locale, setLocale] = useState(() => detectLanguage());

  const { language } = useSelector(configSelector);

  const loadingState = useRef({ [locale]: new Set() });
  const loader = useCallback(
    (path: string) => {
      const [filename] = path.split('.');

      if (loadingState.current[locale].has(filename)) {
        return;
      }
      loadingState.current[locale]?.add(filename);

      const updateTranslations = (content: object) =>
        setTranslationsByLocale((prev) => ({
          ...prev,
          [locale]: {
            ...prev[locale],
            [filename]: content,
          },
        }));

      loadLanguage(locale, filename).then(updateTranslations, () => {
        if (BUILD_ENV === 'dev') {
          // eslint-disable-next-line
          console.error('i18n: no translation file found', filename);
        }
        loadLanguage('en', filename).then(updateTranslations);
      });
    },
    [locale],
  );

  const updateLocale = useCallback(
    (newLocale: string) => {
      if (!translationsByLocale[newLocale]) {
        loadingState.current[newLocale] = new Set();
      }

      setLocale(newLocale);
    },
    [translationsByLocale],
  );

  useEffect(() => {
    const { language: queryLang } = getQueryParamsFromUrl(
      document.location.href,
    );
    const newLocale = detectLanguage((queryLang as string) || language);

    loadingState.current[newLocale] = new Set();

    setLocale(newLocale);
  }, [language]);

  return (
    <TranslationContext.Provider
      value={createGetKey(
        translationsByLocale[locale],
        locale,
        loader,
        updateLocale,
      )}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export default Trans;
